import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { actualizarPerfilTienda } from "../../../services/LoginUserTienda";
import {
  getPasswordChecks,
  getPasswordStrength,
  isPasswordValid,
  PASSWORD_ERROR,
} from "../../../utils/passwordRules";

const ACCENT = "#7c3aed";

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("tienda_data") || "null");
  } catch {
    return null;
  }
};

function UserProfilePage() {
  const [user] = useState(readUser);
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    currentPassword: "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });
    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  }, []);

  useEffect(() => {
    setForm({
      nombre: user?.nombre || "",
      apellido: user?.apellido || "",
      correo: user?.correo || "",
      currentPassword: "",
      password: "",
    });
  }, [user]);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const checks = useMemo(() => getPasswordChecks(form.password), [form.password]);
  const strength = useMemo(() => getPasswordStrength(form.password), [form.password]);
  const showPasswordFeedback = form.password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const cambiaPassword = form.password.trim() !== "";
    if (cambiaPassword) {
      if (!form.currentPassword.trim()) {
        showAlert("error", "Ingresa tu contraseña actual para poder cambiarla.");
        return;
      }
      if (!isPasswordValid(form.password)) {
        showAlert("error", PASSWORD_ERROR);
        return;
      }
    }

    const payload = {
      nombre: form.nombre,
      apellido: form.apellido,
      correo: form.correo,
    };
    if (cambiaPassword) {
      payload.password = form.password;
      payload.currentPassword = form.currentPassword;
    }

    setSaving(true);
    try {
      const res = await actualizarPerfilTienda(payload);
      showAlert("success", res?.message || "Perfil actualizado correctamente.");
      setForm((prev) => ({ ...prev, currentPassword: "", password: "" }));
    } catch (err) {
      console.error("Error al actualizar el perfil:", err);
      showAlert("error", err?.message || "No se pudo actualizar el perfil.");
    } finally {
      setSaving(false);
    }
  };

  const initials = useMemo(() => {
    const a = (user?.nombre || "").trim();
    const b = (user?.apellido || "").trim();
    if (!a && !b) return "U";
    return `${a[0] || ""}${b[0] || ""}`.toUpperCase();
  }, [user]);

  return (
    <div style={ui.page}>
      <style>{keyframes}</style>

      <div style={ui.card}>
        <div style={ui.head}>
          <div style={ui.avatar}>{initials}</div>
          <div>
            <h2 style={ui.title}>Mi perfil</h2>
            <p style={ui.subtitle}>
              Actualiza tus datos personales y tu contraseña.
              {user?.rol_nombre ? ` · Rol: ${user.rol_nombre}` : ""}
            </p>
          </div>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "success" ? ui.alertOk : ui.alertError),
            }}
          >
            <span>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)} type="button">
              ✕
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={ui.grid}>
            <div style={ui.field}>
              <label style={ui.label}>Nombre</label>
              <input
                name="nombre"
                value={form.nombre}
                onChange={updateField}
                required
                maxLength={100}
                style={ui.input}
              />
            </div>

            <div style={ui.field}>
              <label style={ui.label}>Apellido</label>
              <input
                name="apellido"
                value={form.apellido}
                onChange={updateField}
                required
                maxLength={100}
                style={ui.input}
              />
            </div>

            <div style={{ ...ui.field, gridColumn: "1 / -1" }}>
              <label style={ui.label}>Correo electrónico</label>
              <input
                type="email"
                name="correo"
                value={form.correo}
                onChange={updateField}
                required
                maxLength={120}
                style={ui.input}
              />
            </div>

            <div style={{ ...ui.field, gridColumn: "1 / -1" }}>
              <label style={ui.label}>Nueva contraseña</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={updateField}
                style={ui.input}
                placeholder="Déjala vacía para mantener la actual"
                autoComplete="new-password"
              />

              {showPasswordFeedback && (
                <div style={ui.pwBox}>
                  <div style={ui.pwBars}>
                    {[1, 2, 3].map((seg) => (
                      <div
                        key={seg}
                        style={{
                          ...ui.pwBar,
                          background: strength.level >= seg ? strength.color : "#e5e7eb",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ ...ui.pwLevel, color: strength.color }}>
                    Seguridad: {strength.label}
                  </div>
                  <ul style={ui.pwRules}>
                    <Rule ok={checks.length}>Al menos 8 caracteres</Rule>
                    <Rule ok={checks.upper}>Una mayúscula (A-Z)</Rule>
                    <Rule ok={checks.lower}>Una minúscula (a-z)</Rule>
                    <Rule ok={checks.number}>Un número (0-9)</Rule>
                  </ul>
                </div>
              )}
            </div>

            {showPasswordFeedback && (
              <div style={{ ...ui.field, gridColumn: "1 / -1" }}>
                <label style={ui.label}>Contraseña actual</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={form.currentPassword}
                  onChange={updateField}
                  style={ui.input}
                  placeholder="Confirma tu contraseña actual para guardar el cambio"
                  autoComplete="current-password"
                />
                <span style={{ fontSize: "12px", color: "#94a3b8", marginTop: "6px" }}>
                  Necesaria solo para cambiar la contraseña.
                </span>
              </div>
            )}
          </div>

          <div style={ui.footer}>
            <button type="submit" style={{ ...ui.saveBtn, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <li style={{ ...ui.pwRule, color: ok ? "#16a34a" : "#9ca3af" }}>
      <span style={{ fontWeight: 800, width: 14, display: "inline-block" }}>
        {ok ? "✓" : "○"}
      </span>
      <span>{children}</span>
    </li>
  );
}

const keyframes = `
@keyframes perfilIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

const ui = {
  page: {
    padding: "10px 0",
    fontFamily: "'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100dvh - 40px)",
  },
  card: {
    width: "100%",
    flex: 1,
    boxSizing: "border-box",
    background: "#ffffff",
    border: "1px solid #ececf1",
    borderRadius: "16px",
    boxShadow: "0 8px 30px rgba(15, 23, 42, 0.06)",
    padding: "30px 32px",
    animation: "perfilIn 0.4s ease",
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "22px",
  },
  avatar: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "20px",
    flex: "0 0 auto",
    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.30)",
  },
  title: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: "#1e293b",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: "13.5px",
    color: "#64748b",
  },
  alert: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "11px 14px",
    borderRadius: "12px",
    border: "1px solid",
    marginBottom: "18px",
    fontSize: "13.5px",
    fontWeight: 600,
  },
  alertOk: { background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" },
  alertError: { background: "#FEF2F2", borderColor: "#FECACA", color: "#7F1D1D" },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
    color: "inherit",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    color: "#374151",
    marginBottom: "6px",
    fontWeight: 600,
  },
  input: {
    padding: "11px 13px",
    borderRadius: "10px",
    border: "1px solid #D1D5DB",
    background: "#F8FAFC",
    color: "#1F2937",
    fontSize: "15px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  pwBox: { marginTop: "10px" },
  pwBars: { display: "flex", gap: "5px", marginBottom: "6px" },
  pwBar: { flex: 1, height: "5px", borderRadius: "4px", transition: "background 0.2s ease" },
  pwLevel: { fontSize: "12px", fontWeight: 700, marginBottom: "8px" },
  pwRules: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "4px 14px",
  },
  pwRule: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    transition: "color 0.2s ease",
  },
  footer: {
    marginTop: "24px",
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid #f1f1f4",
    paddingTop: "18px",
  },
  saveBtn: {
    padding: "11px 22px",
    background: ACCENT,
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
    transition: "0.2s ease",
  },
};

export default UserProfilePage;
